// composables/usePermission.js
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { isRtuUser } from '@/utils/roles'

export const usePermission = () => {
  const store = useStore()
  
  // ✅ 强制刷新计数器，用于触发 computed 重新计算
  const refreshKey = ref(0)
  
  // ✅ 用户信息（安全访问）
  const user = computed(() => {
    refreshKey.value // 依赖刷新键
    return store.state?.user?.user || null
  })
  
  const menus = computed(() => {
    refreshKey.value
    return store.state?.user?.menus || []
  })
  
  const permissions = computed(() => {
    refreshKey.value
    return store.state?.user?.permissions || []
  })
  
  // ===== 核心权限检查方法 =====
  // local-test：dev/zzhang-fix 服务端未实现 user-full-permissions 接口，
  // 权限数据永远为空 —— 为空时放行（与旧版部署行为一致），接口上线后删除此判断。
  // 受限角色（RTU User）例外：权限缺失时一律拒绝（fail-closed）。
  const noPermissionData = () => {
    if (isRtuUser()) return false // 受限角色不放行；空 map 走正常判定 → false
    const permissionMap = store.state?.user?.permissionMap || {}
    return Object.keys(permissionMap).length === 0
  }

  const hasPermission = (permission) => {
    if (!permission) return true
    // 直接从 store 读取最新的 permissionMap    
    let permissionMap = store.state?.user?.permissionMap || {}

      // 如果 store 中的 permissionMap 为空，从 sessionStorage 获取
    if (!permissionMap || Object.keys(permissionMap).length === 0) {
        const permissionMapStr = sessionStorage.getItem('permissionMap')
        if (permissionMapStr) {
            try {
                permissionMap = JSON.parse(permissionMapStr)
                // ✅ 缓存到 store，避免重复读取 sessionStorage
                if (store.state?.user) {
                    store.state.user.permissionMap = permissionMap
                }
            } catch (e) {
                permissionMap = {}
            }
        }
    }
    return permissionMap[permission] === true
  }

  const hasAnyPermission = (permissionList) => {
    if (!permissionList || permissionList.length === 0) return true
    if (noPermissionData()) return true
    const permissionMap = store.state?.user?.permissionMap || {}
    return permissionList.some(p => permissionMap[p] === true)
  }

  const hasAllPermissions = (permissionList) => {
    if (!permissionList || permissionList.length === 0) return true
    if (noPermissionData()) return true
    const permissionMap = store.state?.user?.permissionMap || {}
    return permissionList.every(p => permissionMap[p] === true)
  }
  
  // ===== 项目权限 =====
  const canViewProject = computed(() => {
    refreshKey.value
    return hasPermission('project:view')
  })
  const canAddProject = computed(() => {
    refreshKey.value
    return hasPermission('project:add')
  })
  const canEditProject = computed(() => {
    refreshKey.value
    return hasPermission('project:edit')
  })
  const canDeleteProject = computed(() => {
    refreshKey.value
    return hasPermission('project:delete')
  })
  const canMonitorProject = computed(() => {
    refreshKey.value
    return hasPermission('project:monitor')
  })
  const canQueryProject = computed(() => {
    refreshKey.value
    return hasPermission('project:query')
  })
  
  // ===== 组织权限 =====
  const canViewOrganization = computed(() => {
    refreshKey.value
    return hasPermission('organization:view')
  })
  const canAddOrganization = computed(() => {
    refreshKey.value
    return hasPermission('organization:add')
  })
  const canEditOrganization = computed(() => {
    refreshKey.value
    return hasPermission('organization:edit')
  })
  const canDeleteOrganization = computed(() => {
    refreshKey.value
    return hasPermission('organization:delete')
  })
  
  // ===== 用户权限 =====
  const canViewUser = computed(() => {
    refreshKey.value
    return hasPermission('user:view')
  })
  const canAddUser = computed(() => {
    refreshKey.value
    return hasPermission('user:add')
  })
  const canEditUser = computed(() => {
    refreshKey.value
    return hasPermission('user:edit')
  })
  const canDeleteUser = computed(() => {
    refreshKey.value
    return hasPermission('user:delete')
  })
  
  // ===== 角色权限 =====
  const canViewRole = computed(() => {
    refreshKey.value
    return hasPermission('role:view')
  })
  const canAddRole = computed(() => {
    refreshKey.value
    return hasPermission('role:add')
  })
  const canEditRole = computed(() => {
    refreshKey.value
    return hasPermission('role:edit')
  })
  const canDeleteRole = computed(() => {
    refreshKey.value
    return hasPermission('role:delete')
  })
  const canConfigPermission = computed(() => {
    refreshKey.value
    return hasPermission('role:permission')
  })
  
  // ===== 设备列表权限 =====
  const canViewDeviceList = computed(() => {
    refreshKey.value
    return hasPermission('deviceList:view')
  })
  const canAddDeviceList = computed(() => {
    refreshKey.value
    return hasPermission('deviceList:add')
  })
  const canEditDeviceList = computed(() => {
    refreshKey.value
    return hasPermission('deviceList:edit')
  })
  const canDeleteDeviceList = computed(() => {
    refreshKey.value
    return hasPermission('deviceList:delete')
  })
  const canExportDeviceList = computed(() => {
    refreshKey.value
    return hasPermission('deviceList:export')
  })
  const canImportDeviceList = computed(() => {
    refreshKey.value
    return hasPermission('deviceList:import')
  })

  // ===== 设备模板权限 =====
  const canViewDeviceTemplate = computed(() => {
    refreshKey.value
    return hasPermission('deviceTemplate:view')
  })
  const canAddDeviceTemplate = computed(() => {
    refreshKey.value
    return hasPermission('deviceTemplate:add')
  })
  const canEditDeviceTemplate = computed(() => {
    refreshKey.value
    return hasPermission('deviceTemplate:edit')
  })
  const canDeleteDeviceTemplate = computed(() => {
    refreshKey.value
    return hasPermission('deviceTemplate:delete')
  })
  const canExportDeviceTemplate = computed(() => {
    refreshKey.value
    return hasPermission('deviceTemplate:export')
  })
  const canImportDeviceTemplate = computed(() => {
    refreshKey.value
    return hasPermission('deviceTemplate:import')
  })

  // ===== 设备网关权限 =====
  const canViewDeviceGateway = computed(() => {
    refreshKey.value
    return hasPermission('deviceGateway:view')
  })
  const canAddDeviceGateway = computed(() => {
    refreshKey.value
    return hasPermission('deviceGateway:add')
  })
  const canEditDeviceGateway = computed(() => {
    refreshKey.value
    return hasPermission('deviceGateway:edit')
  })
  const canDeleteDeviceGateway = computed(() => {
    refreshKey.value
    return hasPermission('deviceGateway:delete')
  })
  const canExportDeviceGateway = computed(() => {
    refreshKey.value
    return hasPermission('deviceGateway:export')
  })
  const canImportDeviceGateway = computed(() => {
    refreshKey.value
    return hasPermission('deviceGateway:import')
  })

  // ===== 设备控制权限 =====
  const canViewDeviceControl = computed(() => {
    refreshKey.value
    return hasPermission('deviceControl:view')
  })
  const canSendDeviceControl = computed(() => {
    refreshKey.value
    return hasPermission('deviceControl:send')
  })
  const canAddDeviceControl = computed(() => {
    refreshKey.value
    return hasPermission('deviceControl:add')
  })
  const canExportDeviceControl = computed(() => {
    refreshKey.value
    return hasPermission('deviceControl:export')
  })

  // ===== 设备升级权限 =====
  const canViewDeviceUpgrade = computed(() => {
    refreshKey.value
    return hasPermission('deviceUpgrade:view')
  })
  const canAddDeviceUpgrade = computed(() => {
    refreshKey.value
    return hasPermission('deviceUpgrade:add')
  })
  const canEditDeviceUpgrade = computed(() => {
    refreshKey.value
    return hasPermission('deviceUpgrade:edit')
  })
  const canDeleteDeviceUpgrade = computed(() => {
    refreshKey.value
    return hasPermission('deviceUpgrade:delete')
  })
  const canExportDeviceUpgrade = computed(() => {
    refreshKey.value
    return hasPermission('deviceUpgrade:export')
  })
  const canImportDeviceUpgrade = computed(() => {
    refreshKey.value
    return hasPermission('deviceUpgrade:import')
  })

  // ===== 场景组态权限 =====
  const canViewSceneConfig = computed(() => {
    refreshKey.value
    return hasPermission('sceneConfig:view')
  })
  const canAddSceneConfig = computed(() => {
    refreshKey.value
    return hasPermission('sceneConfig:add')
  })
  const canEditSceneConfig = computed(() => {
    refreshKey.value
    return hasPermission('sceneConfig:edit')
  })
  const canDeleteSceneConfig = computed(() => {
    refreshKey.value
    return hasPermission('sceneConfig:delete')
  })
  const canDesignSceneConfig = computed(() => {
    refreshKey.value
    return hasPermission('sceneConfig:design')
  })
  const canShareSceneConfig = computed(() => {
    refreshKey.value
    return hasPermission('sceneConfig:share')
  })

  // ===== 独立组态权限 =====
  const canViewIndependentConfig = computed(() => {
    refreshKey.value
    return hasPermission('independentConfig:view')
  })
  const canAddIndependentConfig = computed(() => {
    refreshKey.value
    return hasPermission('independentConfig:add')
  })
  const canEditIndependentConfig = computed(() => {
    refreshKey.value
    return hasPermission('independentConfig:edit')
  })
  const canDeleteIndependentConfig = computed(() => {
    refreshKey.value
    return hasPermission('independentConfig:delete')
  })
  const canDesignIndependentConfig = computed(() => {
    refreshKey.value
    return hasPermission('independentConfig:design')
  })
  const canShareIndependentConfig = computed(() => {
    refreshKey.value
    return hasPermission('independentConfig:share')
  })

  // ===== 实时数据权限 =====
  const canViewRealtimeData = computed(() => {
    refreshKey.value
    return hasPermission('realtimeData:view')
  })
  const canQueryRealtimeData = computed(() => {
    refreshKey.value
    return hasPermission('realtimeData:query')
  })
  const canSetRealtimeData = computed(() => {
    refreshKey.value
    return hasPermission('realtimeData:set')
  })
  const canExportRealtimeData = computed(() => {
    refreshKey.value
    return hasPermission('realtimeData:export')
  })

  // ===== 实时Plot权限 =====
  const canViewRealtimePlot = computed(() => {
    refreshKey.value
    return hasPermission('realtimePlot:view')
  })
  const canQueryRealtimePlot = computed(() => {
    refreshKey.value
    return hasPermission('realtimePlot:query')
  })
  const canSetRealtimePlot = computed(() => {
    refreshKey.value
    return hasPermission('realtimePlot:set')
  })
  const canExportRealtimePlot = computed(() => {
    refreshKey.value
    return hasPermission('realtimePlot:export')
  })

  // ===== 历史数据权限 =====
  const canViewHistoryData = computed(() => {
    refreshKey.value
    return hasPermission('historyData:view')
  })
  const canQueryHistoryData = computed(() => {
    refreshKey.value
    return hasPermission('historyData:query')
  })
  const canExportHistoryData = computed(() => {
    refreshKey.value
    return hasPermission('historyData:export')
  })

  // ===== 报警数据权限 =====
  const canViewAlarmData = computed(() => {
    refreshKey.value
    return hasPermission('alarmData:view')
  })
  const canQueryAlarmData = computed(() => {
    refreshKey.value
    return hasPermission('alarmData:query')
  })
  const canAddAlarmData = computed(() => {
    refreshKey.value
    return hasPermission('alarmData:add')
  })
  const canEditAlarmData = computed(() => {
    refreshKey.value
    return hasPermission('alarmData:edit')
  })
  const canDeleteAlarmData = computed(() => {
    refreshKey.value
    return hasPermission('alarmData:delete')
  })
  const canExportAlarmData = computed(() => {
    refreshKey.value
    return hasPermission('alarmData:export')
  })

  // ===== 操作日志权限 =====
  const canViewOperationLog = computed(() => {
    refreshKey.value
    return hasPermission('operationLog:view')
  })

  // ===== 仪表盘权限 =====
  const canViewDashboard = computed(() => {
    refreshKey.value
    return hasPermission('dashboard:view')
  })

  // ===== 兼容旧代码的别名 =====
  const hasAddPermission = computed(() => {
    refreshKey.value
    return hasPermission('project:add')
  })
  const hasEditPermission = computed(() => {
    refreshKey.value
    return hasPermission('project:edit')
  })
  const hasDeletePermission = computed(() => {
    refreshKey.value
    return hasPermission('project:delete')
  })
  const hasMonitorPermission = computed(() => {
    refreshKey.value
    return hasPermission('project:monitor')
  })

  // ===== 强制刷新方法 =====
  const refresh = () => {
    refreshKey.value++
    console.log('🔄 权限数据已刷新，当前版本号:', refreshKey.value)
    console.log('📋 当前权限列表:', store.state?.user?.permissions || [])
    console.log('📋 当前权限Map:', store.state?.user?.permissionMap || {})
  }

  // ===== 监听权限变化自动刷新 =====
  watch(
    () => store.state?.user?.permissions,
    (newPermissions, oldPermissions) => {
      if (newPermissions !== oldPermissions) {
        console.log('📌 检测到权限数据变化，自动刷新')
        refresh()
      }
    },
    { deep: true, immediate: false }
  )

  // ===== 监听用户登录状态变化 =====
  watch(
    () => store.state?.user?.user,
    (newUser, oldUser) => {
      if (newUser?.id !== oldUser?.id) {
        console.log('📌 检测到用户变化，自动刷新权限')
        refresh()
      }
    },
    { deep: true }
  )

  return {
    // 用户信息
    user,
    menus,
    permissions,
    
    // 通用权限检查方法
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    
    // 强制刷新方法
    refresh,
    
    // 项目权限
    canViewProject,
    canAddProject,
    canEditProject,
    canDeleteProject,
    canMonitorProject,
    canQueryProject,
    
    // 组织权限
    canViewOrganization,
    canAddOrganization,
    canEditOrganization,
    canDeleteOrganization,
    
    // 用户权限
    canViewUser,
    canAddUser,
    canEditUser,
    canDeleteUser,
    
    // 角色权限
    canViewRole,
    canAddRole,
    canEditRole,
    canDeleteRole,
    canConfigPermission,
    
    // 设备列表权限
    canViewDeviceList,
    canAddDeviceList,
    canEditDeviceList,
    canDeleteDeviceList,
    canExportDeviceList,
    canImportDeviceList,

    // 设备模板权限
    canViewDeviceTemplate,
    canAddDeviceTemplate,
    canEditDeviceTemplate,
    canDeleteDeviceTemplate,
    canExportDeviceTemplate,
    canImportDeviceTemplate,

    // 设备网关权限
    canViewDeviceGateway,
    canAddDeviceGateway,
    canEditDeviceGateway,
    canDeleteDeviceGateway,
    canExportDeviceGateway,
    canImportDeviceGateway,

    // 设备控制权限
    canViewDeviceControl,
    canSendDeviceControl,
    canAddDeviceControl,
    canExportDeviceControl,

    // 设备升级权限
    canViewDeviceUpgrade,
    canAddDeviceUpgrade,
    canEditDeviceUpgrade,
    canDeleteDeviceUpgrade,
    canExportDeviceUpgrade,
    canImportDeviceUpgrade,

    // 场景组态权限
    canViewSceneConfig,
    canAddSceneConfig,
    canEditSceneConfig,
    canDeleteSceneConfig,
    canDesignSceneConfig,
    canShareSceneConfig,

    // 独立组态权限
    canViewIndependentConfig,
    canAddIndependentConfig,
    canEditIndependentConfig,
    canDeleteIndependentConfig,
    canDesignIndependentConfig,
    canShareIndependentConfig,

    // 实时数据权限
    canViewRealtimeData,
    canQueryRealtimeData,
    canSetRealtimeData,
    canExportRealtimeData,
    
    // 实时Plot权限
    canViewRealtimePlot,
    canQueryRealtimePlot,
    canSetRealtimePlot,
    canExportRealtimePlot,
    
    // 历史数据权限
    canViewHistoryData,
    canQueryHistoryData,
    canExportHistoryData,
    
    // 报警数据权限
    canViewAlarmData,
    canQueryAlarmData,
    canAddAlarmData,
    canEditAlarmData,
    canDeleteAlarmData,
    canExportAlarmData,

    // 操作日志权限
    canViewOperationLog,

    // 仪表盘权限
    canViewDashboard,
    
    // 兼容旧代码
    hasAddPermission,
    hasEditPermission,
    hasDeletePermission,
    hasMonitorPermission
  }
}